import React, { ReactElement } from 'react';
import { Filter, Table, TableContainer } from '@odin-labs/components';
import { paginationSizePerPage as limit } from 'utils/constants';
import {
  TempBadgeStatus,
  useGetJobsiteContainerTempBadgesQuery,
} from 'apollo/generated/client-operations';
import { usePageQueryParams } from 'utils/usePageQueryParams';
import { LoadingError } from 'components/loadingError';
import { JobsiteTabProps } from 'containers/jobsite/types';
import {
  DeactivateTempBadgeModal,
  ViewTempBadgeModal,
  ReturnTempBadgeModal,
} from 'containers/jobsite/tabs/jobsiteTempBadges/modals';
import { isQueryLoading, useModalState } from 'utils';
import { JobsiteTempBadgesFilters, TempBadgeModalValue } from './types';
import { useColumns, getFilterItems } from './tables';

export function JobsiteTempBadgesTabContainer(
  props: JobsiteTabProps,
): ReactElement {
  const { jobsiteId, onTabApiChange } = props;
  const { page, search, tempBadgeStatuses, showDeactivatedBadges, updateUrl } =
    usePageQueryParams();

  const offset = page * limit;

  const [tempBadgesPollInterval, setTempBadgesPollInterval] =
    React.useState<number>(0);

  const { data, error, refetch, networkStatus } =
    useGetJobsiteContainerTempBadgesQuery({
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      pollInterval: tempBadgesPollInterval,
      variables: {
        jobsiteId,
        jobsiteTempBadgesInput: {
          syncPrintJob: true,
          statuses: tempBadgeStatuses,
          showDeactivatedBadges,
          search,
          paginationInput: { limit, offset },
        },
      },
    });

  // Enable polling interval if there are any temp badges in "Printing" status
  React.useEffect(() => {
    const newTempBadgesPollInterval = data?.getJobsite?.tempBadges?.edges.some(
      ({ node }) => node.status === TempBadgeStatus.Printing,
    )
      ? 5000
      : 0;
    if (newTempBadgesPollInterval !== tempBadgesPollInterval) {
      setTempBadgesPollInterval(newTempBadgesPollInterval);
    }
  }, [
    data,
    // ignore `tempBadgesPollInterval` because it's set by this effect
  ]);

  const {
    value: tempBadgeModalValue,
    isModalOpen,
    openModal,
    closeModal,
    resetModalValue: resetTempBadgeModalValue,
  } = useModalState<TempBadgeModalValue>(null);
  const { tempBadge, modalAction } = tempBadgeModalValue ?? {};

  const jobsite = data?.getJobsite;

  const columns = useColumns({ openModal, jobsiteId: jobsite?.jobsiteId });
  const filterItems = React.useMemo(
    () => getFilterItems({ search, tempBadgeStatuses, showDeactivatedBadges }),
    [search, tempBadgeStatuses, showDeactivatedBadges],
  );

  const onPageChangeHandler = (_pageSize: number, pageIndex: number): void => {
    updateUrl({
      page: pageIndex ? pageIndex + 1 : null,
    });
  };

  const onFiltersChangeHandler = (
    changedFilters: Partial<JobsiteTempBadgesFilters>,
  ): void => {
    updateUrl({ page: null, ...changedFilters });
  };

  const tempBadges = jobsite?.tempBadges.edges.map((c) => c.node) ?? [];
  const tempBadgesCount = jobsite?.tempBadges?.count ?? 0;
  const pageCount = Math.ceil(tempBadgesCount / limit);
  const tempBadgesCountText = jobsite ? tempBadgesCount.toString() : '...';

  React.useEffect(
    () => onTabApiChange({ refetchData: refetch, tempBadgesCountText }),
    [refetch, tempBadgesCountText],
  );

  const closeModalAndRefetch = (): void => {
    closeModal();
    refetch();
  };

  if (error) {
    return <LoadingError error={error} />;
  }

  const loading = isQueryLoading(networkStatus);

  return (
    <TableContainer className="jobsite-temporary-badges-container">
      <Filter
        items={filterItems}
        loading={loading}
        firstItemOnRight="search"
        onChange={onFiltersChangeHandler}
      />
      <Table
        loading={loading}
        columns={columns}
        data={tempBadges}
        initialState={{ pageSize: limit }}
        pageCount={pageCount}
        pageIndex={page}
        remote
        enablePagination
        // onRowClick={({ data: tb }) =>
        //   openModal({ modalAction: 'view', tempBadge: tb })
        // }
        onPageChange={onPageChangeHandler}
        disableGlobalFilter
      />
      <ViewTempBadgeModal
        isOpen={isModalOpen && modalAction === 'view'}
        closeModal={closeModal}
        onClosed={resetTempBadgeModalValue}
        tempBadge={tempBadge}
      />
      <DeactivateTempBadgeModal
        isOpen={isModalOpen && modalAction === 'deactivate'}
        closeModal={closeModal}
        onClosed={resetTempBadgeModalValue}
        tempBadge={tempBadge}
        onConfirm={closeModalAndRefetch}
      />
      <ReturnTempBadgeModal
        isOpen={isModalOpen && modalAction === 'return'}
        closeModal={closeModal}
        onClosed={resetTempBadgeModalValue}
        tempBadge={tempBadge}
        onConfirm={closeModalAndRefetch}
      />
    </TableContainer>
  );
}
